import { CctvResponse, HookResponse } from 'modules/maps';
import { useEffect } from 'react';
import {
  createMarkerContainer,
  createMarkerImg,
  createLinkBadge,
  createTooltip,
  showTooltip,
  hideTooltip
} from '../utils.ts';
import { ptwMapImages } from '../../../../../../../constants';

type HookMarkerProps = {
  map: google.maps.Map | null;
  isLoaded: boolean;
  hookList: HookResponse[];
  onSelectHook?: (hook: HookResponse | null) => void;
};

export function HookMarker({ map, isLoaded, hookList, onSelectHook }: HookMarkerProps) {
  useEffect(() => {
    if (!isLoaded || !map) return;
    if (!google.maps.marker?.AdvancedMarkerElement) return;

    const mapDiv = map.getDiv();
    const tooltip = createTooltip();
    mapDiv.appendChild(tooltip);

    const markers: google.maps.marker.AdvancedMarkerElement[] = [];

    hookList
      .filter((h) => Number.isFinite(h.lat) && Number.isFinite(h.lng))
      .forEach((h) => {
        //console.log(333, h.zone);
        const label = h.deviceName || h.name || 'Hook';
        const container = createMarkerContainer(40);
        if (h.zone === 1) {
          container.appendChild(createMarkerImg(ptwMapImages.hookDanger, label, 40));
        } else if (h.zone === 0) {
          container.appendChild(createMarkerImg(ptwMapImages.hookSafety, label, 40));
        } else {
          container.appendChild(createMarkerImg(ptwMapImages.hookNormal, label, 40));
        }
        const linkBadge = createLinkBadge(h.ptwNo, ptwMapImages.link);
        if (linkBadge) container.appendChild(linkBadge);

        const tooltipRows = [
          { label: 'Device Name', value: h.deviceName },
          { label: 'Name', value: h.name },
          { label: 'Status', value: h.zoneNm },
          ...(h.ptwNo ? [{ label: 'PTW', value: h.ptwNo }] : [])
        ];

        container.addEventListener('mouseenter', (e: MouseEvent) => {
          const rect = mapDiv.getBoundingClientRect();
          showTooltip(tooltip, tooltipRows, e.clientX - rect.left, e.clientY - rect.top);
        });
        container.addEventListener('mousemove', (e: MouseEvent) => {
          const rect = mapDiv.getBoundingClientRect();
          showTooltip(tooltip, tooltipRows, e.clientX - rect.left, e.clientY - rect.top);
        });
        container.addEventListener('mouseleave', () => hideTooltip(tooltip));

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: h.lat, lng: h.lng },
          title: label,
          content: container,
          gmpClickable: true
        });
        const handler = () => {
          onSelectHook?.(h);
        };
        marker.addEventListener('gmp-click', handler);
        markers.push(marker);
      });

    return () => {
      markers.forEach((m) => (m.map = null));
      tooltip.remove();
    };
  }, [map, isLoaded, hookList]);

  return null;
}
